// ============================================================
// TMR Portal Admin — como o rating de crédito se lê na tela (26)
// ============================================================
import type { BureauNegativeView, BureauOccurrenceView, BureauReportView, ClinicView, DebtView } from './contract';
import { formatDay } from '../businessRules/status';

/** How each reason the rating held a sale back reads. */
const REASON_LABEL: Record<string, string> = {
  nome_divergente: 'Nome diferente do cadastro',
  nascimento_divergente: 'Nascimento diferente do cadastro',
  divida_inadimplente: 'Dívida inadimplente na TMR',
  sem_score: 'Sem score (Serasa indisponível)',
  faixa_score: 'Faixa de score',
};

/**
 * A reason as the operator reads it. A reason this screen has no copy for — the v1.1 added it first — reads as
 * itself rather than disappearing.
 *
 * @param reason The v1.1's code.
 */
export function reasonLabel(reason: string): string {
  return REASON_LABEL[reason] ?? reason;
}

/** The bureau the registration came from, as the screen names it. */
export function registrationSourceLabel(source: string): string {
  return source === 'CpfCnpj' ? 'Receita Federal (CPF.CNPJ)' : 'Serasa';
}

/**
 * Whether who was born on `birthDate` is under 18 on `today` — the check the v1.1 runs, so the screen can say the
 * clinic is contesting a minor. A 29 February birthday completes on 1 March in a common year.
 *
 * @param birthDate yyyy-mm-dd.
 */
export function isMinor(birthDate: string, today: Date = new Date()): boolean {
  const [year, month, day] = birthDate.split('-').map(Number);
  if (!year || !month || !day) return false;
  const adult = new Date(year + 18, month - 1, day);
  // 29/02 + 18 years rolls to 1/03 in a common year — the same day the Civil Code counts.
  const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return todayLocal < adult;
}

/** How each situation of an evaluation reads. */
const SITUATION_LABEL: Record<string, string> = {
  pendente: 'Aguardando a TMR',
  liberada: 'Liberada pelo rating',
  aprovada: 'Aprovada pela TMR',
  reprovada: 'Reprovada pela TMR',
  substituida: 'Substituída por outra análise',
  encerrada: 'Venda cancelada',
};

/** @param situation The legacy situation code. */
export function situationLabel(situation: string): string {
  return SITUATION_LABEL[situation] ?? situation;
}

/** The situations whose reading is good news, for the badge's tone. */
export function situationTone(situation: string): 'ok' | 'warn' | 'bad' | 'neutral' {
  switch (situation) {
    case 'pendente':
      return 'warn';
    case 'liberada':
    case 'aprovada':
      return 'ok';
    case 'reprovada':
      return 'bad';
    default:
      return 'neutral';
  }
}

/** How each negative section of a Serasa report reads; an unknown section keeps the bureau's name. */
const NEGATIVE_LABEL: Record<string, string> = {
  pefin: 'Pendências financeiras (Pefin)',
  refin: 'Pendências bancárias (Refin)',
  notary: 'Protestos',
  check: 'Cheques sem fundo',
  collectionRecords: 'Ações de cobrança',
  lawsuit: 'Ações judiciais',
};

/** @param negative A section of the report. */
export function negativeLabel(negative: Pick<BureauNegativeView, 'kind'>): string {
  return NEGATIVE_LABEL[negative.kind] ?? negative.kind;
}

/** The short name of each section, for a table column and a filter; an unknown section keeps the bureau's name. */
const NEGATIVE_SHORT_LABEL: Record<string, string> = {
  pefin: 'Pefin',
  refin: 'Refin',
  notary: 'Protesto',
  check: 'Cheque',
  collectionRecords: 'Cobrança',
  lawsuit: 'Ação judicial',
};

/** @param kind The section, by the bureau's name. */
export function negativeShortLabel(kind: string): string {
  return NEGATIVE_SHORT_LABEL[kind] ?? kind;
}

/** One occurrence of a report with the section it came from — a row of the occurrences table. */
export interface OccurrenceRow extends BureauOccurrenceView {
  kind: string;
}

/**
 * Every occurrence of a report in one list, newest first — an undated one last, since nothing says where it falls.
 *
 * @param report The report.
 */
export function occurrenceRows(report: Pick<BureauReportView, 'negatives'>): OccurrenceRow[] {
  return report.negatives
    .flatMap((negative) => negative.occurrences.map((occurrence) => ({ ...occurrence, kind: negative.kind })))
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
}

/** A section with something in it — the ones that count as a restriction. */
export function hasRestriction(negative: Pick<BureauNegativeView, 'count' | 'occurrences'>): boolean {
  return (negative.count ?? 0) > 0 || negative.occurrences.length > 0;
}

/** A yyyy-mm-dd date as the screens read it, or `—` without one. Noon keeps the day whatever the browser's zone. */
export function dayLabel(date: string | null | undefined): string {
  return date ? formatDay(`${date}T12:00:00`) : '—';
}

/** Where an occurrence happened: `Campinas/SP`, either part alone, or `—`. */
export function placeLabel(occurrence: Pick<BureauOccurrenceView, 'city' | 'state'>): string {
  const city = occurrence.city?.trim();
  const state = occurrence.state?.trim();
  if (city && state) return `${city}/${state}`;
  return city || state || '—';
}

/** The clinic's name, or its producer id when the V2 does not know it yet. */
export function clinicLabel(clinic: ClinicView | null): string {
  if (!clinic) return '—';
  return clinic.name ?? `Clínica ${clinic.legacyProducerId} (sem cadastro no V2)`;
}

/**
 * How long a sale has waited, as the queue reads it: `há 3 h`, `há 2 dias`.
 *
 * @param iso When it was sent to the TMR.
 * @param now The clock, for tests.
 */
export function waitingLabel(iso: string, now: Date = new Date()): string {
  const since = new Date(iso).getTime();
  if (Number.isNaN(since)) return '—';

  const minutes = Math.max(0, Math.floor((now.getTime() - since) / 60_000));
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;

  const days = Math.floor(hours / 24);
  return days === 1 ? 'há 1 dia' : `há ${days} dias`;
}

/** An installment as the debts list reads it: `Contrato 4321 · parcela 3` (the down payment is `entrada`). */
export function installmentLabel(debt: Pick<DebtView, 'orderId' | 'installment'>): string {
  return `Contrato ${debt.orderId} · ${debt.installment === 0 ? 'entrada' : `parcela ${debt.installment}`}`;
}

/** Days late as a sentence: `em dia`, `vence em breve`, `45 dias de atraso`. */
export function daysLateLabel(debt: Pick<DebtView, 'daysLate'>): string {
  if (debt.daysLate <= 0) return 'em dia';
  return debt.daysLate === 1 ? '1 dia de atraso' : `${debt.daysLate} dias de atraso`;
}

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** @param amount A value in reais. */
export function money(amount: number | null | undefined): string {
  return amount == null ? '—' : currency.format(amount);
}

/**
 * A CPF as people type it, keeping only digits and adding the mask as they go — the field's display, never what
 * travels (the backend normalises either).
 *
 * @param raw What the operator typed.
 */
export function maskCpf(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}
